import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import {
  getPermissionsCatalog,
  getRolePermissions,
  getRoles,
  updateRolePermissions,
} from "../../services/roleAccessService";
import { extractApiErrorMessage } from "../../utils/masterHelpers";
import PermissionMatrix from "./components/PermissionMatrix";
import RoleTabs from "./components/RoleTabs";
import {
  buildPermissionKey,
  getActionColumns,
  getPermissionRows,
  isPermissionChecked,
  togglePermission,
} from "./roleAccessHelpers";
import "./RoleAccess.css";
import "./new_RoleAccess.css";

export default function RoleAccessPage() {
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState("");
  const [isBaseLoading, setIsBaseLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [permissionError, setPermissionError] = useState("");
  const [permissionKeys, setPermissionKeys] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState("");
  const [savedPermissionKeys, setSavedPermissionKeys] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [toast, setToast] = useState({ message: "", open: false, severity: "success" });
  const selectedRoleIdRef = useRef("");

  const showToast = useCallback((message, severity = "success") => {
    setToast({ message, open: true, severity });
  }, []);

  const actionColumns = useMemo(() => getActionColumns(catalog), [catalog]);
  const permissionRows = useMemo(() => getPermissionRows(catalog), [catalog]);
  const permissionSet = useMemo(() => new Set(permissionKeys), [permissionKeys]);
  const savedPermissionSet = useMemo(
    () => new Set(savedPermissionKeys),
    [savedPermissionKeys]
  );
  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) || null,
    [roles, selectedRoleId]
  );

  useEffect(() => {
    selectedRoleIdRef.current = selectedRoleId;
  }, [selectedRoleId]);

  const isDirty = useMemo(() => {
    if (permissionKeys.length !== savedPermissionKeys.length) {
      return true;
    }

    return permissionKeys.some((key) => !savedPermissionSet.has(key));
  }, [permissionKeys, savedPermissionKeys, savedPermissionSet]);

  const loadBaseData = useCallback(
    async (preferredRoleId) => {
      setIsBaseLoading(true);
      setRolesError("");
      setCatalogError("");

      try {
        const [rolesResponse, catalogResponse] = await Promise.all([
          getRoles(),
          getPermissionsCatalog(),
        ]);
        const nextRoles = Array.isArray(rolesResponse) ? rolesResponse : [];
        const nextCatalog = Array.isArray(catalogResponse) ? catalogResponse : [];

        setRoles(nextRoles);
        setCatalog(nextCatalog);

        if (nextRoles.length === 0) {
          setSelectedRoleId("");
          setPermissionKeys([]);
          setSavedPermissionKeys([]);
          return;
        }

        const fallbackRoleId =
          preferredRoleId ||
          (nextRoles.some((role) => role.id === selectedRoleIdRef.current)
            ? selectedRoleIdRef.current
            : nextRoles[0].id);

        setSelectedRoleId(fallbackRoleId);
      } catch (apiError) {
        const message = extractApiErrorMessage(
          apiError,
          "Failed to load roles and permissions catalog."
        );
        setRolesError(message);
        setCatalogError(message);
        setRoles([]);
        setCatalog([]);
        setSelectedRoleId("");
        setPermissionKeys([]);
        setSavedPermissionKeys([]);
      } finally {
        setIsBaseLoading(false);
      }
    },
    []
  );

  const loadRolePermissions = useCallback(async (roleId) => {
    if (!roleId) {
      setPermissionKeys([]);
      setSavedPermissionKeys([]);
      setPermissionError("");
      return;
    }

    setPermissionsLoading(true);
    setPermissionError("");

    try {
      const response = await getRolePermissions(roleId);
      const nextPermissionKeys = (Array.isArray(response) ? response : [])
        .filter((permission) => permission?.resource && permission?.action)
        .map((permission) => buildPermissionKey(permission.resource, permission.action));

      setPermissionKeys(nextPermissionKeys);
      setSavedPermissionKeys(nextPermissionKeys);
    } catch (apiError) {
      const message = extractApiErrorMessage(
        apiError,
        "Failed to load role permissions."
      );
      setPermissionError(message);
      setPermissionKeys([]);
      setSavedPermissionKeys([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    loadRolePermissions(selectedRoleId);
  }, [loadRolePermissions, selectedRoleId]);

  const handleRoleSelect = (role) => {
    if (!role?.id || role.id === selectedRoleId) {
      return;
    }

    if (
      isDirty &&
      !window.confirm("You have unsaved permission changes. Switch roles and discard them?")
    ) {
      return;
    }

    setSelectedRoleId(role.id);
  };

  const handleTogglePermission = (resource, action) => {
    setPermissionKeys((currentKeys) => {
      const nextKeys = togglePermission(new Set(currentKeys), resource, action);
      return Array.from(nextKeys);
    });
  };

  const handleResetChanges = () => {
    setPermissionKeys(savedPermissionKeys);
  };

  const handleSave = async () => {
    if (!selectedRoleId || !isDirty) {
      return;
    }

    setIsSaving(true);

    try {
      const permissions = permissionKeys.map((key) => {
        const separatorIndex = key.indexOf(":");

        return {
          action: key.slice(separatorIndex + 1),
          resource: key.slice(0, separatorIndex),
        };
      });

      await updateRolePermissions(selectedRoleId, { permissions });
      showToast("Permissions updated successfully.", "success");
      await loadRolePermissions(selectedRoleId);
    } catch (apiError) {
      showToast(
        extractApiErrorMessage(apiError, "Failed to update permissions."),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderBody = () => {
    if (isBaseLoading) {
      return (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading role access</h2>
          <p className="masters-state__text">
            Please wait while roles and permissions are being prepared.
          </p>
        </div>
      );
    }

    if (rolesError || catalogError) {
      return (
        <div className="masters-state">
          <h2 className="masters-state__title">Unable to load role access</h2>
          <p className="masters-state__text">{rolesError || catalogError}</p>
          <div style={{ marginTop: "16px" }}>
            <button className="masters-btn" onClick={() => loadBaseData()} type="button">
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (roles.length === 0) {
      return (
        <div className="masters-state">
          <h2 className="masters-state__title">No roles available</h2>
          <p className="masters-state__text">
            No role records are available to manage permissions right now.
          </p>
        </div>
      );
    }

    if (catalog.length === 0) {
      return (
        <div className="masters-state">
          <h2 className="masters-state__title">No permissions catalog available</h2>
          <p className="masters-state__text">
            The backend did not return any permission resources yet.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="role-access__selector new_role-access-selector">
          <RoleTabs
            isLoading={false}
            onSelect={handleRoleSelect}
            roles={roles}
            selectedRoleId={selectedRoleId}
          />
        </div>

        {permissionsLoading ? (
          <div className="masters-state">
            <div className="masters-state__loader" />
            <h2 className="masters-state__title">Loading permissions</h2>
            <p className="masters-state__text">
              Fetching assigned permissions for the selected role.
            </p>
          </div>
        ) : permissionError ? (
          <div className="masters-state">
            <h2 className="masters-state__title">Unable to load role permissions</h2>
            <p className="masters-state__text">{permissionError}</p>
            <div style={{ marginTop: "16px" }}>
              <button
                className="masters-btn"
                onClick={() => loadRolePermissions(selectedRoleId)}
                type="button"
              >
                Retry
              </button>
            </div>
          </div>
        ) : permissionRows.length === 0 ? (
          <div className="masters-state">
            <h2 className="masters-state__title">No permission resources found</h2>
            <p className="masters-state__text">
              The selected environment does not have any permission resources to display.
            </p>
          </div>
        ) : (
          <>
            <PermissionMatrix
              actionColumns={actionColumns}
              isPermissionChecked={(resource, action) =>
                isPermissionChecked(permissionSet, resource, action)
              }
              isSaving={isSaving}
              onToggle={handleTogglePermission}
              permissionRows={permissionRows}
            />

            <div className="role-access__footer new_role-access-footer">
              <div className="role-access__footer-text new_role-access-footer-text">
                Changes apply after the affected users log in again or refresh their session.
              </div>
              <div className="role-access__footer-actions new_role-access-footer-actions">
                <button
                  className="masters-btn masters-btn--secondary"
                  disabled={!isDirty || isSaving}
                  onClick={handleResetChanges}
                  type="button"
                >
                  Reset Changes
                </button>
                <button
                  className="masters-btn"
                  disabled={!isDirty || isSaving}
                  onClick={handleSave}
                  type="button"
                >
                  {isSaving
                    ? `Saving ${selectedRole?.label || "Role"}...`
                    : `Save ${selectedRole?.label || "Role"}`}
                </button>
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <MasterShell pageClassName="role-access-module">
      <div className="masters-card role-access new_role-access-list">
        <div className="masters-card__header role-access__header new_role-access-header">
          <div className="new_role-access-header-main">
            <h1 className="masters-card__title">Role Access</h1>
            <p className="masters-card__description">
              Manage role-wise permissions for Muddamal modules.
            </p>
          </div>
          <div className="role-access__selected-role-inline new_role-access-selected-role-inline">
            <span className="role-access__selected-role-inline-item">
              Role: <strong>{selectedRole?.label || "-"}</strong>
            </span>
            <span className="role-access__selected-role-inline-item">
              Scope: {(selectedRole?.scope_type || "global").replace(/_/g, " ")}
            </span>
            <span className="role-access__selected-role-inline-item">
              Level: {selectedRole?.level ?? "-"}
            </span>
          </div>
        </div>

        {renderBody()}
      </div>

      <MasterToast
        message={toast.message}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
